// src/components/import/SavedKitsList.tsx
import React, { useMemo, useState, useEffect } from "react";
import { ChevronDown, MoreVertical, Edit2, Download, Trash2 } from "lucide-react";
import { PacketMeta } from "../../hooks/usePackets";

type Props = {
  packets: PacketMeta[];
  currentPacketId: string | null;
  onSelectAsCurrent: (id: string) => Promise<void>;
  onLoad: (id: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onExport: (id: string) => Promise<void>;
};

const SAVED_KITS_KEY = "cueboard.import.savedKitsOpen";

export default function SavedKitsList({
  packets,
  currentPacketId,
  onSelectAsCurrent,
  onLoad,
  onRename,
  onDelete,
  onExport,
}: Props) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(SAVED_KITS_KEY);
      return v === "1";
    } catch {
      return false;
    }
  });

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = (id: string) => setOpenMenuId((cur) => (cur === id ? null : id));
  const closeMenu = () => setOpenMenuId(null);

  useEffect(() => {
    if (!openMenuId) return;
    const handleMouseDown = (e: MouseEvent) => {
      const root = document.querySelector(`[data-kebab-root="${openMenuId}"]`);
      if (root && e.target instanceof Node && root.contains(e.target)) return;
      setOpenMenuId(null);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  const sorted = useMemo(
    () => [...packets].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)),
    [packets]
  );

  return (
    <div className="card hoverable vstack-sm" style={{ marginTop: 16 }}>
      <button
        type="button"
        className="button button-secondary"
        onClick={() => {
          setOpen((prev) => {
            const next = !prev;
            try {
              localStorage.setItem(SAVED_KITS_KEY, next ? "1" : "0");
            } catch {}
            return next;
          });
        }}
        aria-expanded={open}
        aria-controls="saved-prep-kits"
        data-no-rowselect
      >
        <ChevronDown size={16} className={`chevron ${open ? "expanded" : ""}`} />
        Saved Prep Kits
      </button>

      <div id="saved-prep-kits" style={{ marginTop: 12, display: open ? "block" : "none" }}>
        {sorted.length === 0 ? (
          <p className="muted small">No Prep Kits yet. Create one above or import from DOCX.</p>
        ) : (
          <ul className="list vstack-sm">
            {sorted.map((p) => {
              const isCurrent = currentPacketId === p.id;
              return (
                <li
                  key={p.id}
                  className={`list-row ${isCurrent ? "is-current" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-current={isCurrent ? "true" : undefined}
                  title={isCurrent ? "Current Prep Kit" : "Click to set as current"}
                  onClick={() => onSelectAsCurrent(p.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectAsCurrent(p.id);
                    }
                  }}
                >
                  <div className="flex-1">
                    <div style={{ fontWeight: 600 }}>
                      {p.name} {isCurrent ? <span className="text-success">(current)</span> : null}
                    </div>
                    <div className="small muted">Updated {new Date(p.updatedAt).toLocaleString()}</div>
                  </div>

                  <div className="actions" style={{ alignItems: "center", gap: 8 }}>
                    <button
                      className="button button-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLoad(p.id);
                      }}
                      data-no-rowselect
                    >
                      Load
                    </button>

                    <div style={{ position: "relative" }} data-kebab-root={p.id} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="button button-secondary"
                        aria-haspopup="menu"
                        aria-expanded={openMenuId === p.id}
                        aria-controls={`menu-${p.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(p.id);
                        }}
                        title="More actions"
                        data-no-rowselect
                      >
                        <MoreVertical size={16} />
                      </button>

                      {openMenuId === p.id && (
                        <div
                          id={`menu-${p.id}`}
                          role="menu"
                          aria-label="Prep Kit actions"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "calc(100% + 6px)",
                            minWidth: 180,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                            padding: 6,
                            zIndex: 20,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="button button-ghost"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeMenu();
                              onRename(p.id, p.name);
                            }}
                            style={{ width: "100%", justifyContent: "flex-start" }}
                            data-no-rowselect
                          >
                            <Edit2 size={14} className="mr-2" /> Rename
                          </button>
                          <button
                            className="button button-ghost"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeMenu();
                              onExport(p.id);
                            }}
                            style={{ width: "100%", justifyContent: "flex-start" }}
                            data-no-rowselect
                          >
                            <Download size={14} className="mr-2" /> Export
                          </button>
                          <button
                            className="button button-ghost"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeMenu();
                              onDelete(p.id);
                            }}
                            style={{ width: "100%", justifyContent: "flex-start", color: "#b91c1c" }}
                            data-no-rowselect
                          >
                            <Trash2 size={14} className="mr-2" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
